import { useState } from 'react'
import firebase from '../../firebase/compat'
import ReadOnlyGuard from '../common/ReadOnlyGuard'
import LETTER_PLACEHOLDERS from '../../helpers/letters/letterPlaceholders'
import { DEFAULT_KEY } from '../../helpers/letters/mergeLetterTemplates'

// Kop en tekst van een brief aanpassen op de briefpagina (Letter.jsx) en per
// organisatie bewaren via de Cloud Function saveLetterTemplate — LetterTemplates
// is voor de browser alleen-lezen. De concepttekst staat in Letter, omdat het
// voorbeeld eronder hem ook toont; hier alleen de velden en het opslaan.
// AI-CONTEXT: See docs/features/adres-koppeling-algemene-qr.md
// AI-CONTEXT: See docs/pitfalls.md#state-updater-reads-event-target-too-late

const GENERIC_ERROR = 'Opslaan is niet gelukt. Je tekst staat er nog; probeer het opnieuw.'

/**
 * @typedef {object} LetterText
 * @property {string} Title
 * @property {string} Body
 */

/**
 * @param {object} props
 * @param {string} props.client - CompagnyID van de organisatie
 * @param {import('../../helpers/letters/mergeLetterTemplates').LetterTemplate} props.template - De gekozen brief zoals hij opgeslagen is
 * @param {LetterText} props.text - Wat er nu in de velden staat
 * @param {(text: LetterText) => void} props.onChange - Bij elke wijziging in kop of tekst
 * @param {() => void} props.onReset - Na het terugzetten naar de standaardtekst
 */
const LetterEditor = ({ client, template, text, onChange, onReset }) => {
    const [saving, setSaving] = useState(false)
    const [saved, setSaved] = useState(false)
    const [saveError, setSaveError] = useState('')

    const dirty = text.Title !== (template.Title || '') || text.Body !== (template.Body || '')
    // Een opgeslagen eigen versie van de standaardbrief (en niet die uit de app).
    const canReset = template.TemplateKey === DEFAULT_KEY && Boolean(template.CompagnyID)
    const placeholders = LETTER_PLACEHOLDERS[template.MomentKind] || []

    const call = async (payload) => {
        if (saving) return false
        setSaving(true)
        setSaved(false)
        setSaveError('')
        try {
            await firebase.app().functions('europe-west1').httpsCallable('saveLetterTemplate')({
                client, key: template.TemplateKey, kind: template.MomentKind, ...payload,
            })
            return true
        } catch (error) {
            console.error('Brief opslaan mislukt', error)
            // De server zegt bij een afgewezen tekst precies wat er mis is. Firebase 8
            // geeft de code zonder 'functions/'-voorvoegsel.
            setSaveError(error?.code === 'invalid-argument' ? error.message : GENERIC_ERROR)
            return false
        } finally {
            setSaving(false)
        }
    }

    const saveHandler = async () => {
        if (await call({ Title: text.Title, Body: text.Body })) setSaved(true)
    }

    const resetHandler = async () => {
        if (await call({ reset: true })) onReset()
    }

    const titleChangeHandler = (e) => {
        const value = e.target.value
        setSaved(false)
        onChange({ ...text, Title: value })
    }

    const bodyChangeHandler = (e) => {
        const value = e.target.value
        setSaved(false)
        onChange({ ...text, Body: value })
    }

    return (
        <div className="letter-editor">
            <ReadOnlyGuard edit="measurement.edit">
                <label>
                    Kop
                    <input type="text" value={text.Title} onChange={titleChangeHandler} />
                </label>
                <label>
                    Tekst
                    <textarea rows={12} value={text.Body} onChange={bodyChangeHandler} />
                </label>
                <p className="letter-editor-hint">
                    {`Invulplekken: ${placeholders.map((p) => `{{${p}}}`).join(', ')}`}
                </p>
                <div className="letter-editor-actions">
                    <button type="button" onClick={saveHandler} disabled={!dirty || saving}>
                        {saving ? 'Opslaan…' : 'Opslaan'}
                    </button>
                    {canReset && (
                        <button type="button" onClick={resetHandler} disabled={saving}>
                            Standaardtekst terugzetten
                        </button>
                    )}
                    {saved && <span role="status">Opgeslagen</span>}
                </div>
                {saveError && <p className="letter-error" role="alert">{saveError}</p>}
                {/* Zonder deze regel durfde een gebruiker niet op te slaan (testronde 2026-10-30, B6). */}
                <p className="letter-editor-hint">Printen kan zonder opslaan. &apos;Opslaan&apos; bewaart deze tekst voor iedereen in je organisatie.</p>
            </ReadOnlyGuard>
        </div>
    )
}

export default LetterEditor
